import React, { useContext } from "react";
import useSpacing from "../../core/hooks/useSpacing";
import { Link } from "react-router-dom";
import useBaseStyle from "../../core/hooks/useBaseStyle";
import useImageStyle from "../../core/hooks/useImageStyle";
import useContainerStyle from "../../core/hooks/useContainerStyle";
import useBreakpoint from "../../core/hooks/useBreakpoint";
import { getBackgroundPatternClass } from "../../core/utils/backgroundPatternUtils";
import { buildTargetPageUrl } from "../../core/utils/urlUtils";
import TextItem from "./Text";
import { getIcon } from "../../editor/utils/icons";
import Background from "./Background";
import { EditContext } from "../../editor/context/EditContext";
import { usePreviewOptional } from "../../editor/context/PreviewContext";
import { getContainerInsetShadowClass } from '../../core/utils/containerStyleUtils';
import { resolveTextPosition } from "../../core/utils/characterStyleUtils";

// Map backend icon choices to our icon set keys (keys match iconMap entries)
const ICON_NAME_MAP = {
  "happy face": "star",
  star: "star",
  link: "link",
  "external link": "external link",
  "right arrow": "right arrow",
  "left arrow": "left arrow",
  "up arrow": "up arrow",
  "down arrow": "down arrow",
  home: "home",
  mail: "mail",
  phone: "phone",
  download: "download",
  play: "play",
  pause: "pause",
  calendar: "calendar",
  location: "location",
  "google scholar": "scholar",
  linkedin: "linkedin",
  facebook: "facebook",
  instagram: "instagram",
  tiktok: "tiktok",
  github: "github",
};

const LINK_CHOICES = {
  INTERNAL: "internal",
  EXTERNAL: "external",
};

const buildButtonClasses = (
  combinedClasses,
  baseClasses,
  containerStyleClasses,
  useFixedWidth,
  justifyClass
) => {
  // When fixed width is enabled, remove w-auto to allow custom width
  const widthClass = useFixedWidth ? "" : "w-auto";
  
  return [
    combinedClasses,
    baseClasses,
    containerStyleClasses,
    widthClass,
    `relative overflow-hidden inline-flex ${justifyClass} transition duration-200 ease-in-out`,
  ]
    .filter(Boolean)
    .join(" ");
};

const buildContent = (item, iconSize = 20) => {
  const mapped = ICON_NAME_MAP[(item.icon || "").toLowerCase()];
  
  // Apply icon size if provided
  const iconStyle = {
    width: `${iconSize}px`,
    height: `${iconSize}px`,
    minWidth: `${iconSize}px`, // Prevent shrinking
    minHeight: `${iconSize}px`,
  };
  
  const iconEl = mapped ? (
    <span 
      className="inline-flex items-center mr-1 shrink-0 align-middle [&>svg]:w-full [&>svg]:h-full" 
      style={iconStyle}
    >
      {getIcon(mapped)}
    </span>
  ) : null;

  return item.icon_before_text ? (
    <>
      {iconEl}
      {item.text && <TextItem item={item.text} returnDiv={false} />}
    </>
  ) : (
    <>
      {item.text && <TextItem item={item.text} returnDiv={false} />}
      {iconEl}
    </>
  );
};

const ButtonContent = ({
  buttonClasses,
  baseStyles,
  bgImage,
  imageStyleClasses,
  imageInlineStyles,
  colorClass,
  colorStyle,
  jsPatternLayer,
  content,
  insetShadowClass,
  buttonWidth,
  useFixedWidth,
  textAlignClass,
  buttonType = "button",
  onPointerDown,
  onMouseDown,
}) => {
  // Merge button width into inline styles if provided
  const buttonStyles = buttonWidth 
    ? { ...baseStyles, width: buttonWidth }
    : baseStyles;
  
  return (
    <button 
      type={buttonType} 
      className={buttonClasses} 
      style={buttonStyles}
      onPointerDown={onPointerDown}
      onMouseDown={onMouseDown}
    >
      <Background
        bgImage={bgImage}
        imageStyleClasses={imageStyleClasses}
        imageInlineStyles={imageInlineStyles}
        colorClass={colorClass}
        colorStyle={colorStyle}
        jsPatternLayer={jsPatternLayer}
        hoverClass={`${insetShadowClass} group-hover/button:brightness-75`}
      />
      {/* Remove whitespace-nowrap when fixed width is enabled to allow text wrapping */}
      <span className={`relative z-10 flex items-center gap-2 ${!useFixedWidth ? 'whitespace-nowrap' : textAlignClass}`}>
        {content}
      </span>
    </button>
  );
};

const ButtonItem = ({ item }) => {
  const editContext = useContext(EditContext);
  const preview = usePreviewOptional();
  const currentBreakpoint = useBreakpoint();
  
  // Ignore spacing when button is rendered inside a ButtonCollection so gap controls spacing
  // Preserve padding but strip margin when inside a ButtonCollection.
  // useSpacing signature: (item, ignoreSpacing=false). We keep spacing and manually remove margin classes.
  const { combinedClasses } = useSpacing(item);
  const classesNoMargin = combinedClasses
    .split(" ")
    .filter((c) => !/^(m|mt|mr|mb|ml|mx|my)-/.test(c))
    .join(" ");
  const { styleClasses: baseClasses, inlineStyles: baseStyles } =
    useBaseStyle(item);
  const containerClasses = useContainerStyle(item);
  const { styleClasses: imageClasses, inlineStyles: imageStyles } =
    useImageStyle(item);

  // Handle null/undefined items gracefully
  if (!item) {
    return null;
  }

  // Calculate responsive button width if fixed width is enabled
  const getButtonWidth = () => {
    if (!item.use_fixed_width || !item.fixed_width) {
      return null; // Auto width
    }
    
    // Get width for current breakpoint, fallback to 150px
    const widthPx = item.fixed_width[currentBreakpoint] || 150;
    return `${widthPx}px`;
  };

  const buttonWidth = getButtonWidth();

  const showImage = item.background?.show_image;
  const bgImage =
    showImage && item.background?.background_image?.image
      ? item.background.background_image.image
      : null;
  const bgPattern = item.background?.background_pattern;

  const { colorClass = "", colorStyle = {}, jsPatternLayer = null } = bgImage
    ? {}
    : getBackgroundPatternClass(bgPattern);
  const insetShadowClass = getContainerInsetShadowClass(item?.container_style);

  // Calculate alignment classes based on the button's text position.
  // ``text_position`` is now a ``ResponsiveTextPosition`` wrapper (object
  // with per-breakpoint keys); resolve it to the matching string for the
  // current viewport before splitting. Strings still pass through (legacy
  // payloads / hand-rolled test fixtures).
  const textPosition = resolveTextPosition(
    item?.text?.character_style?.text_position,
  );
  // Format is "vertical-horizontal" e.g. "middle-center"
  const horizontalAlignment = textPosition ? textPosition.split("-")[1] : "center";
  
  const alignmentClasses = {
    left: { justify: "justify-start", text: "text-left" },
    center: { justify: "justify-center", text: "text-center" },
    right: { justify: "justify-end", text: "text-right" },
    full: { justify: "justify-center", text: "text-justify" }, // Fallback for full
  };
  
  const { justify: justifyClass, text: textAlignClass } = alignmentClasses[horizontalAlignment] || alignmentClasses.center;

  const buttonClasses = buildButtonClasses(
    item?.button_collection ? classesNoMargin : combinedClasses,
    baseClasses,
    containerClasses,
    item.use_fixed_width,
    justifyClass
  );
  const iconSize = item.icon_size || 20;
  const content = buildContent(item, iconSize);
  const iconStyle = item.icon_color ? { color: item.icon_color } : undefined;
  const isSubmitButton = !!item?.is_submit_button;
  const targetPage = buildTargetPageUrl(item);
  const isExternal = item.link_choice?.toLowerCase() === LINK_CHOICES.EXTERNAL;
  const handlePreviewNavigate = preview?.onNavigate;

  if (isSubmitButton) {
    return (
      <div>
        <div className="item button-item flex justify-center">
          <div className="group/button">
            <ButtonContent
              buttonClasses={buttonClasses}
              baseStyles={baseStyles}
              bgImage={bgImage}
              imageStyleClasses={imageClasses}
              imageInlineStyles={imageStyles}
              colorClass={colorClass}
              colorStyle={colorStyle}
              jsPatternLayer={jsPatternLayer}
              insetShadowClass={insetShadowClass}
              content={<span style={iconStyle}>{content}</span>}
              buttonWidth={buttonWidth}
              useFixedWidth={item.use_fixed_width}
              textAlignClass={textAlignClass}
              buttonType={editContext ? "button" : "submit"}
              onPointerDown={editContext ? undefined : (e) => {
                // Stop drag listeners from interfering with button clicks (non-edit mode only)
                e.stopPropagation();
              }}
              onMouseDown={editContext ? undefined : (e) => {
                // Stop drag listeners from interfering with button clicks (non-edit mode only)
                e.stopPropagation();
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="item button-item flex justify-center">
        <Link
          to={targetPage}
          className="group/button"
          onClick={(e) => {
            if (editContext) {
              e.preventDefault();
              // Don't stop propagation — let the click bubble to EditableElement for selection
              return;
            }
            if (handlePreviewNavigate && !isExternal) {
              e.preventDefault();
              e.stopPropagation();
              handlePreviewNavigate(targetPage);
              return;
            }
          }}
          onPointerDown={editContext ? undefined : (e) => {
            // Stop drag listeners from interfering with button clicks (non-edit mode only)
            e.stopPropagation();
          }}
          onMouseDown={editContext ? undefined : (e) => {
            // Stop drag listeners from interfering with button clicks (non-edit mode only)
            e.stopPropagation();
          }}
          {...(isExternal && { target: "_blank", rel: "noopener noreferrer" })}
        >
          <ButtonContent
            buttonClasses={buttonClasses}
            baseStyles={baseStyles}
            bgImage={bgImage}
            imageStyleClasses={imageClasses}
            imageInlineStyles={imageStyles}
            colorClass={colorClass}
            colorStyle={colorStyle}
            jsPatternLayer={jsPatternLayer}
            insetShadowClass={insetShadowClass}
            content={<span style={iconStyle}>{content}</span>}
            buttonWidth={buttonWidth}
            useFixedWidth={item.use_fixed_width}
            textAlignClass={textAlignClass}
          />
        </Link>
      </div>
    </div>
  );
};

export default ButtonItem;
